import { browser } from '$app/environment';
import { goto, invalidate } from '$app/navigation';
import { resolve as resolvePaths } from '$app/paths';
import { useQueuedCallback } from '$lib/utils/queuedCallbacks.svelte';
import { SvelteURLSearchParams } from 'svelte/reactivity';

let loginWindow: WindowProxy | null = null;

const queuedCallback = useQueuedCallback();

function openAuthWindow(url: string): WindowProxy | null {
	if (!browser) {
		throw new Error('must be called on the client');
	}

	const windowWidth = 500;
	const windowHeight = 610;
	const windowTop = (window.innerHeight - windowHeight) / 2 + window.screenY;
	const windowLeft = (window.innerWidth - windowWidth) / 2 + window.screenX;

	return window.open(
		url,
		`michelinid`,
		`top=${windowTop},left=${windowLeft},height=${windowHeight},width=${windowWidth},scrollbars=no,status=no,resizable=yes,toolbar=no,menubar=no,directories=no,location=no`,
	);
}

async function onLoginMessage(e: MessageEvent) {
	const { source, origin } = e;
	// security measure, we only accept messages from the login window
	// and only if it is back on our own origin
	if (source !== loginWindow || origin !== window.origin) {
		return;
	}
	const { login, gotoPage } = e.data;
	if (login === true) {
		window.removeEventListener('message', onLoginMessage);
		loginWindow?.close();
		window.focus();
		await invalidate('app:login').finally(() => {
			if (!queuedCallback.isQueueEmpty()) {
				queuedCallback.runQueue();
			}

			if (gotoPage) {
				goto(resolvePaths(gotoPage));
			}
		});
	}
}
/**
 * custom popup signin
 *
 * [Documentation](https://authjs.dev/reference/sveltekit/client#signin)
 */
export async function signIn(gotoPage?: string) {
	if (!browser) {
		throw new Error('must be called on the client');
	}
	if (loginWindow) {
		loginWindow.close();
	}
	window.addEventListener('message', onLoginMessage);

	const searchParams = new SvelteURLSearchParams();

	if (gotoPage) {
		searchParams.set('gotoPage', gotoPage);
	}

	loginWindow = openAuthWindow(`/authentication/login?${searchParams.toString()}`);

	if (!loginWindow || loginWindow.closed || typeof loginWindow.closed == 'undefined') {
		throw new Error('popup blocked'); // TODO nicer message
	} else {
		loginWindow.focus();
	}
}

/**
 * Signs the user out, by removing the session cookie.
 * Automatically adds the CSRF token to the request.
 *
 * [Documentation](https://authjs.dev/reference/sveltekit/client#signout)
 */
export async function signOut(gotoPage: string) {
	if (!browser) {
		throw new Error('must be called on the client');
	}

	const res = await fetch(`/auth/signout`, {
		method: 'post',
		headers: {
			'Content-Type': 'application/x-www-form-urlencoded',
			'X-Auth-Return-Redirect': '1',
		},
	});

	if (res.ok) {
		// add an iframe to the page to trigger the logout on the auth server
		const logoutUrl = `/authentication/sf-signout`;

		await new Promise((resolve) => {
			const iframe = document.createElement('iframe');
			iframe.src = logoutUrl;
			iframe.width = '0';
			iframe.height = '0';
			iframe.tabIndex = -1;
			iframe.ariaHidden = 'true';
			iframe.onload = () => {
				iframe.remove();
				resolve(null);
			};
			iframe.onerror = () => {
				// TODO: what should we do in case of errors ?
				iframe.remove();
				resolve(null);
			};
			document.body.appendChild(iframe);
		});

		if (gotoPage) {
			// goto first to prevent profile page with logged out state showing
			goto(resolvePaths(gotoPage as any)).finally(() => {
				invalidate('app:login');
			});
		} else {
			await invalidate('app:login');
		}
	} else {
		throw new Error('logout failed'); // TODO nicer message
	}
}
